import {
  ADAPTER_EVENTS,
  SafeEventEmitterProvider,
  WALLET_ADAPTER_TYPE,
  WALLET_ADAPTERS
} from '@web3auth/base';
import { Web3AuthCore } from '@web3auth/core';
import type { LOGIN_PROVIDER_TYPE } from '@toruslabs/openlogin';

import { OpenloginAdapter } from '@web3auth/openlogin-adapter';
import React, {
  createContext,
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { CHAIN_CONFIG, CHAIN_CONFIG_TYPE } from '../config/ChainConfig';
import { WEB3AUTH_NETWORK_TYPE } from '../config/Web3AuthNetwork';

import { providers, Contract, Wallet, Signer, utils } from 'ethers';
import { getIssuer } from '../services/user/badge';

const DiplomadeContractAddress = '0x28595a70AB69ad8827897B4FA48dda55DC32F8B8';
const DiplomadeContractChainId = '5';

export type User = {
  aggregateVerifier: string;
  email: string;
  idToken: string;
  issuerStatus: string;
  issuerDetails: IssuerDetails | null;
  name: string;
  oAuthAccessToken: string;
  oAuthIdToken: string;
  profileImage: string;
  signer: Signer;
  typeOfLogin: string;
  verifier: string;
  verifierId: string;
  walletAddress: string;
};

export type IssuerDetails = {
  id: string;
  data: IssuerDetailsData;
  status: string;
};
export type IssuerDetailsData = {
  issuerName: string;
  signature: string;
  issuerAddress: string;
};

export interface IWeb3AuthContext {
  web3Auth: Web3AuthCore | null;
  provider: providers.Provider | null;
  isLoading: boolean;
  isInitialized: boolean;
  user: User;
  chain: string;
  setUser: (user: User) => void;
  login: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  setIsLoading: (loading: boolean) => void;
}

export const Web3AuthContext = createContext<IWeb3AuthContext>({
  web3Auth: null,
  provider: null,
  isLoading: true,
  isInitialized: false,
  user: null,
  chain: '',
  setUser: (user: User) => {},
  setIsLoading: (loading: boolean) => {},
  login: async (idToken: string) => {},
  logout: async () => {}
});

export function useWeb3Auth(): IWeb3AuthContext {
  return useContext(Web3AuthContext);
}

interface IWeb3AuthState {
  web3AuthNetwork: WEB3AUTH_NETWORK_TYPE;
  chain: CHAIN_CONFIG_TYPE;
  children?: React.ReactNode;
}
interface IWeb3AuthProps {
  children?: React.ReactNode | any;
  web3AuthNetwork: WEB3AUTH_NETWORK_TYPE;
  chain: CHAIN_CONFIG_TYPE;
}

export const Web3AuthProvider: FunctionComponent<IWeb3AuthState> = ({
  children,
  web3AuthNetwork,
  chain
}: IWeb3AuthProps) => {
  const [web3Auth, setWeb3Auth] = useState<Web3AuthCore | null>(null);
  const [provider, setProvider] = useState<providers.Provider | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const subscribeAuthEvents = (web3auth: Web3AuthCore) => {
      // Can subscribe to all ADAPTER_EVENTS and LOGIN_MODAL_EVENTS

      web3auth.on(ADAPTER_EVENTS.CONNECTED, async (data: unknown) => {
        console.log('successfully logged in', data);
        const _userInfo = await web3auth.getUserInfo();
        const _user: any = { ..._userInfo };
        // console.log('user', _userInfo, _user);

        if (_userInfo) {
          const _provider = new providers.Web3Provider(web3auth.provider);

          const signer = _provider.getSigner();

          const address = await signer.getAddress();
          _user.signer = signer;
          _user.walletAddress = address;

          const issuerDetails = await getIssuer(address);
          _user.issuerDetails = issuerDetails || null;
          _user.issuerStatus = issuerDetails.status ?? 'NOT_ISSUER';

          setProvider(_provider);
          setUser(_user);
          setIsLoading(false);
        }
      });

      web3auth.on(ADAPTER_EVENTS.CONNECTING, () => {
        console.log('connecting');
      });

      web3auth.on(ADAPTER_EVENTS.DISCONNECTED, () => {
        console.log('disconnected');
        setUser(null);
      });

      web3auth.on(ADAPTER_EVENTS.ERRORED, (error) => {
        console.error('some error or user has cancelled login request', error);
      });
    };

    async function init() {
      try {
        // setIsLoading(true);
        // get your client id from https://dashboard.web3auth.io by registering a plug and play application.
        const clientId = process.env.REACT_APP_WEB3AUTH_CLIENT_ID;
        const currentChainConfig = CHAIN_CONFIG[chain];

        const web3AuthInstance = new Web3AuthCore({
          chainConfig: currentChainConfig,
          clientId
        });
        subscribeAuthEvents(web3AuthInstance);
        const adapter = new OpenloginAdapter({
          adapterSettings: {
            network: web3AuthNetwork,
            clientId,
            uxMode: 'redirect',
            whiteLabel: {
              name: 'Diplomade ',
              logoLight: 'https://web3auth.io/images/w3a-L-Favicon-1.svg',
              logoDark: 'https://web3auth.io/images/w3a-D-Favicon-1.svg',
              defaultLanguage: 'en',
              dark: true // whether to enable dark mode. defaultValue: false
            },

            loginConfig: {
              jwt: {
                name: 'diplomade-demo',
                verifier: 'diplomade-demo',
                typeOfLogin: 'jwt',
                clientId: process.env.REACT_APP_COGNITO_CLIENT_ID
              }
            }
          }
        });
        web3AuthInstance.configureAdapter(adapter);
        await web3AuthInstance.init();

        setWeb3Auth(web3AuthInstance);
      } catch (error) {
        console.error(error);
      } finally {
        setIsInitialized(true);
        // ensure that loading status toggle to false
        // if there is not a user logged in
        setTimeout(() => {
          if (isLoading) setIsLoading(false);
        }, 2000);
      }
    }
    init();
  }, [chain, web3AuthNetwork]);

  const login = async (idToken: string) => {
    try {
      setIsLoading(true);
      if (!web3Auth) {
        console.log('web3auth not initialized yet');
        return;
      }
      const localProvider = await web3Auth.connectTo(
        WALLET_ADAPTERS.OPENLOGIN,
        {
          relogin: true,
          loginSettings: {
            mfaLevel: 'none'
          },
          loginProvider: 'jwt',
          extraLoginOptions: {
            id_token: idToken,
            // this is domain of your app created in aws
            domain: process.env.REACT_APP_COGNITO_DOMAIN,
            // the param/field which you are going to verify from aws cognito
            verifierIdField: 'email',
            // response you want from cognito i.e jwt token
            response_type: 'token',
            //  scope params are those which are enabled in cognito app's "Allowed OAuth Scopes" settings
            scope: 'email openid profile'
          }
        }
      );
      console.log('localProvider', localProvider);
    } catch (error) {
      console.log('error', error);
    }
  };

  const logout = async () => {
    if (!web3Auth) {
      console.log('web3auth not initialized yet');
      return;
    }
    await web3Auth.logout();
    //     window.open("
    //     https://diplomade.auth.us-east-1.amazoncognito.com/logout?
    //     client_id="766gtmguo3d460o3d5hli8dfvj"&
    //     logout_uri=LOGOUT_URI&
    //     redirect_uri=wimdow.location.
    // ");
    setProvider(null);
  };

  const contextProvider = {
    web3Auth,
    chain,
    provider,
    user,
    isLoading,
    isInitialized,
    setUser,
    setIsLoading,
    login,
    logout
  };
  return (
    <Web3AuthContext.Provider value={contextProvider}>
      {children}
    </Web3AuthContext.Provider>
  );
};
